import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Users, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";

const ROLE_LABELS: Record<string, string> = {
  admin: "Administrador",
  operator: "Operador",
  viewer: "Visualização",
};

interface InvitePreview {
  email: string;
  name: string | null;
  role: string;
  supplierName: string;
  used: boolean;
  expired: boolean;
}

const AcceptCollaboratorInvite = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, signUp, signIn, signOut } = useAuth();
  const token = searchParams.get("token") || "";

  const [invite, setInvite] = useState<InvitePreview | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(true);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isAccepting, setIsAccepting] = useState(false);
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  // Uma pessoa pode já ter conta como cliente e estar apenas aceitando acesso
  // de fornecedor com o mesmo e-mail — nesse caso o cadastro falha com
  // "already registered" e devemos oferecer login em vez de erro seco.
  const [authMode, setAuthMode] = useState<"signup" | "login">("signup");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    const loadPreview = async () => {
      if (!token) {
        setPreviewError("Link de convite inválido.");
        setIsLoadingPreview(false);
        return;
      }
      try {
        const { data, error } = await supabase.functions.invoke("accept-collaborator-invite", {
          body: { token, action: "preview" },
        });
        if (error || !data?.success) {
          setPreviewError(data?.error || "Não foi possível carregar este convite.");
        } else {
          setInvite(data.invite);
        }
      } catch (err) {
        console.error("Error loading invite preview:", err);
        setPreviewError("Não foi possível carregar este convite.");
      } finally {
        setIsLoadingPreview(false);
      }
    };
    loadPreview();
  }, [token]);

  const emailMatches = !!(user?.email && invite?.email && user.email.toLowerCase() === invite.email.toLowerCase());

  const handleAccept = async () => {
    setIsAccepting(true);
    try {
      const { data, error } = await supabase.functions.invoke("accept-collaborator-invite", {
        body: { token, action: "accept" },
      });

      if (error || !data?.success) {
        toast.error(data?.error || "Não foi possível aceitar o convite.");
        setIsAccepting(false);
        return;
      }

      toast.success("Convite aceito! Redirecionando para o painel...");
      window.location.href = "/fornecedor/painel/inicio";
    } catch (err) {
      console.error("Error accepting invite:", err);
      toast.error("Não foi possível aceitar o convite.");
      setIsAccepting(false);
    }
  };

  const handleSignUpAndAccept = async () => {
    if (password.length < 6) {
      toast.error("A senha deve ter pelo menos 6 caracteres.");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("As senhas não coincidem.");
      return;
    }
    if (!invite) return;

    setIsSigningUp(true);
    try {
      const { error } = await signUp(invite.email, password, invite.name || invite.email);
      if (error) {
        const alreadyRegistered = /already registered|already exists|já cadastrad/i.test(error.message || "");
        if (alreadyRegistered) {
          toast.info("Este e-mail já tem uma conta — faça login para aceitar o convite.");
          setAuthMode("login");
          setPassword("");
          setConfirmPassword("");
        } else {
          toast.error(error.message || "Não foi possível criar sua conta.");
        }
        setIsSigningUp(false);
        return;
      }
      // Se a confirmação de e-mail estiver ativa, pode não haver sessão ainda —
      // nesse caso pedimos para o usuário confirmar o e-mail e voltar ao link.
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        toast.success("Conta criada! Confirme seu e-mail e volte a este link para concluir.");
        setIsSigningUp(false);
        return;
      }
      await handleAccept();
    } catch (err) {
      console.error("Error signing up:", err);
      toast.error("Não foi possível criar sua conta.");
      setIsSigningUp(false);
    }
  };

  const handleLoginAndAccept = async () => {
    if (!invite) return;
    setIsLoggingIn(true);
    try {
      const { error } = await signIn(invite.email, password);
      if (error) {
        toast.error(error.message || "Senha incorreta.");
        setIsLoggingIn(false);
        return;
      }
      await handleAccept();
    } catch (err) {
      console.error("Error logging in:", err);
      toast.error("Não foi possível entrar na conta.");
      setIsLoggingIn(false);
    }
  };

  const renderContent = () => {
    if (isLoadingPreview) {
      return (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      );
    }

    if (previewError || !invite) {
      return (
        <div className="text-center py-8 space-y-3">
          <AlertCircle className="h-10 w-10 text-destructive mx-auto" />
          <p className="text-foreground font-medium">{previewError || "Convite inválido."}</p>
        </div>
      );
    }

    if (invite.used) {
      return (
        <div className="text-center py-8 space-y-3">
          <CheckCircle2 className="h-10 w-10 text-green-600 mx-auto" />
          <p className="text-foreground font-medium">Este convite já foi utilizado.</p>
          <Button variant="outline" onClick={() => navigate("/login")}>Ir para o login</Button>
        </div>
      );
    }

    if (invite.expired) {
      return (
        <div className="text-center py-8 space-y-3">
          <AlertCircle className="h-10 w-10 text-destructive mx-auto" />
          <p className="text-foreground font-medium">Este convite expirou.</p>
          <p className="text-sm text-muted-foreground">Peça para gerarem um novo convite.</p>
        </div>
      );
    }

    if (user && !emailMatches) {
      return (
        <div className="text-center py-8 space-y-3">
          <AlertCircle className="h-10 w-10 text-amber-600 mx-auto" />
          <p className="text-foreground font-medium">
            Este convite foi enviado para <strong>{invite.email}</strong>, mas você está logado como {user.email}.
          </p>
          <Button variant="outline" onClick={() => signOut()}>Sair da conta atual</Button>
        </div>
      );
    }

    if (user && emailMatches) {
      return (
        <div className="text-center py-6 space-y-4">
          <p className="text-foreground">
            Logado como <strong>{user.email}</strong>. Confirme para aceitar o convite.
          </p>
          <Button onClick={handleAccept} disabled={isAccepting} size="lg" className="w-full">
            {isAccepting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Aceitar Convite
          </Button>
        </div>
      );
    }

    // Não logado, e o e-mail já tem conta (ex.: já é cliente na plataforma):
    // pede a senha existente e faz login em vez de tentar cadastrar de novo.
    if (authMode === "login") {
      return (
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>E-mail</Label>
            <Input value={invite.email} disabled className="bg-muted" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="login-password">Sua senha</Label>
            <Input
              id="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
            />
          </div>
          <Button onClick={handleLoginAndAccept} disabled={isLoggingIn} size="lg" className="w-full">
            {isLoggingIn ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Entrar e aceitar convite
          </Button>
          <p className="text-xs text-muted-foreground text-center">
            Esqueceu a senha? <a href="/esqueci-senha" className="underline">Recuperar senha</a>.
          </p>
        </div>
      );
    }

    // Não logado, sem conta ainda: cria conta com o e-mail do convite e aceita em seguida.
    return (
      <div className="space-y-4 py-2">
        <div className="space-y-1.5">
          <Label>E-mail</Label>
          <Input value={invite.email} disabled className="bg-muted" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="new-password">Crie uma senha</Label>
          <Input
            id="new-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Mínimo 6 caracteres"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="confirm-password">Confirme a senha</Label>
          <Input
            id="confirm-password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </div>
        <Button onClick={handleSignUpAndAccept} disabled={isSigningUp} size="lg" className="w-full">
          {isSigningUp ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Criar conta e aceitar convite
        </Button>
        <p className="text-xs text-muted-foreground text-center">
          Já tem uma conta com este e-mail?{" "}
          <button type="button" className="underline" onClick={() => { setAuthMode("login"); setPassword(""); }}>
            Faça login
          </button>{" "}
          sem sair desta página.
        </p>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
              <Users className="h-6 w-6 text-primary" />
            </div>
            <CardTitle>Convite de Colaborador</CardTitle>
            {invite && !invite.used && !invite.expired && (
              <CardDescription>
                {invite.supplierName} te convidou como <strong>{ROLE_LABELS[invite.role] || invite.role}</strong>
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>{renderContent()}</CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
};

export default AcceptCollaboratorInvite;
